import * as Yup from 'yup';
import { useState, forwardRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
// material
import {
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContentText,
  DialogContent,
  Button,
  Slide
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { ResetPassword } from '../../../network/User';
import { parseValidationErrorMessage } from '../../../utils/Utilities';
import { SnackBarAlert } from '../../HelperComponents';
import { passwordRules } from '../../../URLConstants';


/* eslint-disable */
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function ResetPasswordForm({ token }) {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const [errorData, setErrorData] = useState({
    visible: false,
    message: '',
    title: ''
  });
  const [isSnackBarAlertOpen, setIsSnackBarAlertOpen] = useState(false);
  const [snackBarMetas, setSnackBarMetas] = useState({
    title: '',
    message: '',
    severity: 'info',
    handleClose: null
  });
  const [showRules, setShowRules] = useState(false);

  const ResetPasswordSchema = Yup.object().shape({
    password: Yup.string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters')
      .max(20, 'Password must not exceed 20 characters')
      .matches(/^\S*$/, 'Password must not contain spaces')
      .matches(/[!@#$%^&*]/, 'At least 1 special character required')
      .matches(/(?:.*\d){3,}/, 'At least 3 digits required'),

    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required('Please confirm your password')
  });

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: ''
    },
    validationSchema: ResetPasswordSchema,
    onSubmit: () => {
      if (formik.values.password !== formik.values.confirmPassword) {
        setSnackBarMetas((prevState) => ({
          ...prevState,
          title: "Password and confirm password doesn't match",
          severity: 'error'
        }));
        setIsSnackBarAlertOpen(true);

        formik.setSubmitting(false);
        return;
      }
      ResetPasswordRequest(token, formik.values.password);
    }
  });

  const handleAlertDialogClose = () => {
    setErrorData({ ...errorData, visible: false, message: '' });
  };

  const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps } = formik;

  const showErrorState = Boolean(touched.password && errors.password);

  const isPasswordStrong = passwordRules.every((rule) => rule.test(values.password || ''));

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  function ResetPasswordRequest(token, password) {
    ResetPassword(token, password)
      .then((res) => {
        formik.setSubmitting(false);
        setSnackBarMetas((prevState) => ({
          ...prevState,
          title: 'Your password has been changed successfully !',
          severity: 'success'
        }));
        setIsSnackBarAlertOpen(true);

        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      })
      .catch((err) => {
        const suggestions = err?.response?.data?.suggestions || [];

        const formattedTitle = (
          <>
            {parseValidationErrorMessage(err)}
            {suggestions.map((s, i) => (
              <div key={i}>
                {i + 1}. {s}
              </div>
            ))}
          </>
        );

        formik.setSubmitting(false);

        setSnackBarMetas((prev) => ({
          ...prev,
          title: formattedTitle,
          severity: 'error'
        }));

        setIsSnackBarAlertOpen(true);
      });
  }

  return (
    <div>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              autoComplete="current-password"
              type={showPassword ? 'text' : 'password'}
              onFocus={() => setShowRules(true)}
              label="Password"
              {...getFieldProps('password')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleShowPassword} edge="end">
                      <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              error={Boolean(touched.password && errors.password)}
            // helperText={touched.password && errors.password}
            />

            {showRules && (
              <Stack spacing={1} sx={{ mt: 1 }}>
                {passwordRules.map((rule, index) => {
                  const passed = rule.test(values.password || '');
                  const showError = showErrorState && !passed;

                  return (
                    <Stack key={index} direction="row" spacing={1} alignItems="center">
                      {passed ? (
                        <CheckCircleOutlineOutlinedIcon
                          sx={{ color: 'success.main', fontSize: 18 }}
                        />
                      ) : (
                        <CancelOutlinedIcon
                          sx={{
                            color: showError ? '#FF0000' : 'text.disabled',
                            fontSize: 18
                          }}
                        />
                      )}

                      <span
                        style={{
                          color: passed ? '#54D62C' : showError ? '#FF0000' : '#9e9e9e',
                          fontSize: '14px'
                        }}
                      >
                        {rule.label}
                      </span>
                    </Stack>
                  );
                })}
              </Stack>
            )}

            <TextField
              fullWidth
              autoComplete="current-password"
              type={showPassword ? 'text' : 'password'}
              label="Confirm Password"
              {...getFieldProps('confirmPassword')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleShowPassword} edge="end">
                      <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              error={Boolean(touched.confirmPassword && errors.confirmPassword)}
              helperText={touched.confirmPassword && errors.confirmPassword}
            />
          </Stack>

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={!isPasswordStrong}
            sx={{ mt: 3 }}
          >
            Submit
          </LoadingButton>
        </Form>
      </FormikProvider>

      <Dialog
        open={errorData?.visible}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => handleAlertDialogClose()}
        aria-describedby="iot_res_alert-dialog-slide-description"
      >
        <DialogTitle>{errorData?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="aiot_res_alert-dialog-slide-description">
            {errorData?.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleAlertDialogClose()}>OK</Button>
        </DialogActions>
      </Dialog>

      <SnackBarAlert
        open={isSnackBarAlertOpen}
        title={snackBarMetas?.title}
        message={snackBarMetas?.message}
        severity={snackBarMetas?.severity}
        handleClose={() => setIsSnackBarAlertOpen(false)}
      />
    </div>
  );
}